// Contact detail (docs/06 §2): where a single contact is managed — Call,
// Favorite toggle (device-local), Ban/Unblock (server), and the call log
// filtered to this exact contact. Reached by tapping a contact row or a
// recents row.
//
// Two layouts, one component (web/plan.md §Layout concept): below the `desktop`
// breakpoint it renders the today's full-screen page (a bare screen with a back
// arrow, no bottom nav) — byte-for-byte unchanged so the Capacitor WebView sees
// no regression. At `desktop`+ it renders the pane variant the AppShell mounts
// on the right: a large centred avatar, presence line, action row, and the
// per-contact journal — no back arrow (the sidebar owns navigation there).
//
// Offline-first: the screen boots from the local cache (friend identity, block
// state) and the device-local call log, so it renders fully with the server
// unreachable; fetches only update the view. Call + Favorite work offline
// (favorites are device-local). Block/unblock needs the server; a failure
// surfaces a notice, never a crash.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Friend } from '@merkury/protocol';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { candidateSeed } from '../../avatar/mercury.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { presenceOf } from '@merkury/brain/api/liveEvents.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { PresenceDot } from '../ui/PresenceDot.js';
import { useDialer } from '../ui/useDialer.js';
import { useFavorites } from '../ui/useFavorites.js';
import { useCallLog } from '../ui/useCallLog.js';
import { useIsDesktop } from '../ui/AppShell.js';
import { setAlias, setFavorite } from '../../storage/kvStores.js';
import type { StoredCallLogEntry } from '../../storage/kvStores.js';
import type { PresenceStatus } from '@merkury/protocol';
import {
  getCachedBlocks,
  getCachedFriends,
  setCachedBlocks,
  setCachedFriends,
} from '../../storage/serverCache.js';
import { describeCall, formatTimestamp } from '../ui/callFormat.js';
import { displayNameFor } from '../ui/peerName.js';
import { useAliases } from '../ui/useAliases.js';

/**
 * The device-local call log, filtered to a single contact (newest first, as the
 * log already is). Pure and exported so the per-contact journal filter can be
 * unit-tested without rendering the screen.
 */
export function callsForContact(
  log: readonly StoredCallLogEntry[],
  userId: string,
): readonly StoredCallLogEntry[] {
  return log.filter((entry) => entry.peerUserId === userId);
}

const PRESENCE_LABEL_KEY: Record<PresenceStatus, MessageKey> = {
  app: 'contactDetail.presenceApp',
  reachable: 'contactDetail.presenceReachable',
  offline: 'contactDetail.presenceOffline',
};

// Inline SVG (not emoji) for the desktop action row — same rationale and glyphs
// as the tab bar / in-call controls.
function PhoneIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden="true">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1z" />
    </svg>
  );
}

// Pencil glyph for the Nickname edit affordance — same inline-SVG rationale as
// PhoneIcon/StarIcon (no emoji).
function PencilIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 18.8 6.2 21.9l1.1-6.5L2.6 9.8l6.5-.9z" />
    </svg>
  );
}

export function ContactDetailScreen(): ReactNode {
  const t = useT();
  const isDesktop = useIsDesktop();
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const { api, liveEvents } = useScreens();
  const live = useStore(liveEvents.state);
  const favorites = useFavorites();
  const log = useCallLog();
  const aliases = useAliases();
  const { notice, dial } = useDialer();

  const [friend, setFriend] = useState<Friend | undefined>(() =>
    getCachedFriends()?.data.find((f) => f.userId === userId),
  );
  const [blocked, setBlocked] = useState<boolean>(
    () => getCachedBlocks()?.data.includes(userId) ?? false,
  );
  const [blockError, setBlockError] = useState<string | null>(null);
  const [removeConfirming, setRemoveConfirming] = useState(false);
  const [blockConfirming, setBlockConfirming] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [nicknameEditing, setNicknameEditing] = useState(false);
  const [nicknameValue, setNicknameValue] = useState('');

  // The device-local call log, filtered to this exact contact (newest first).
  const calls = callsForContact(log, userId);

  const reload = useCallback(async () => {
    try {
      const res = await api.listFriends();
      setFriend(res.friends.find((f) => f.userId === userId));
      setCachedFriends(res.friends);
    } catch {
      // keep last known (cache-hydrated; may degrade to the bare UUID)
    }
    try {
      const res = await api.listBlocks();
      setBlocked(res.blocked.includes(userId));
      setCachedBlocks(res.blocked);
    } catch {
      // unknown block state offline — leave last known
    }
  }, [api, userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // The raw UUID stays visible below (an explicitly technical field, like the
  // add-friend paste field or account settings) — this name-facing header
  // never falls back to it (see peerName.ts). `displayNameFor` consults the
  // alias store internally, and subscribing to `aliases` above (useAliases)
  // makes this recompute the moment the Nickname editor below saves or clears.
  const displayName = displayNameFor(
    userId,
    friend ? [friend] : undefined,
    // No live friend (blocked/unfriended): the call log's name snapshot keeps
    // the header human instead of "Unknown caller".
    log.find((e) => e.peerUserId === userId && e.peerName)?.peerName,
  );
  // docs/08 §4: the "aka <real name>" subtitle shows only when an alias is set
  // — and only once the real friend record (not just a call-log snapshot) is
  // known, since an alias can only exist for an actual friend.
  const alias = friend ? aliases[userId] : undefined;
  const presence = presenceOf(live, userId, friend);
  const effectivePresence: PresenceStatus = blocked ? 'offline' : presence;
  const isFav = favorites.includes(userId);
  // No avatar shown when unset — the web header never invents an initials fallback.
  const avatarSeed = friend?.avatarSeed;

  async function toggleBlock(): Promise<void> {
    setBlockError(null);
    const next = !blocked;
    try {
      if (next) await api.block({ userId });
      else await api.unblock(userId);
      setBlocked(next);
      // Keep the cold-start snapshot consistent with what the server now has.
      const cached = getCachedBlocks()?.data ?? [];
      setCachedBlocks(next ? [...cached, userId] : cached.filter((id) => id !== userId));
    } catch {
      setBlockError(
        next ? t('contactDetail.couldNotBlock') : t('contactDetail.couldNotUnblock'),
      );
    }
  }

  // Unfriend is a server-confirmed action (revokes calling both ways) — the
  // server is the source of truth for friendship, so a failure must NOT touch
  // the local cache (distinct from favorites, which are device-local and
  // always succeed offline). Success drops this contact from the friends
  // cache and returns to the list, since there is nothing left to show here.
  async function removeFriend(): Promise<void> {
    setRemoveError(null);
    setRemoving(true);
    try {
      await api.unfriend(userId);
      const cached = getCachedFriends()?.data ?? [];
      setCachedFriends(cached.filter((f) => f.userId !== userId));
      navigate('/contacts', { replace: true });
    } catch {
      setRemoveError(t('contactDetail.couldNotRemoveFriend'));
      setRemoving(false);
    }
  }

  function startCall(): void {
    void dial(
      userId,
      effectivePresence === 'offline'
        ? t('call.offlineHint', { name: displayName })
        : undefined,
    );
  }

  // Nickname (docs/08 §5): device-local, no confirm step, freely reversible.
  // Prefilled with the current alias (empty if none); `setAlias` sanitizes
  // (trim/64-rune cap) and treats an empty value as "clear".
  function startNicknameEdit(): void {
    setNicknameValue(aliases[userId] ?? '');
    setNicknameEditing(true);
  }
  function saveNickname(): void {
    void setAlias(userId, nicknameValue);
    setNicknameEditing(false);
  }
  function clearNickname(): void {
    void setAlias(userId, null);
    setNicknameValue('');
    setNicknameEditing(false);
  }

  // --- Shared sub-views (identical DOM in both layouts) ----------------------

  const noticeView = notice ? (
    <p role="status" className="text-sm text-warn">
      {notice}
    </p>
  ) : null;

  // Inline edit affordance (docs/08 §5) — same "trigger row flips to an inline
  // panel" pattern as blockConfirmView/removeConfirmView below.
  const nicknameEditView = (
    <div className="flex flex-col gap-2">
      <label htmlFor="nickname-input" className="sr-only">
        {t('contactDetail.nickname')}
      </label>
      <input
        id="nickname-input"
        type="text"
        value={nicknameValue}
        onChange={(e) => setNicknameValue(e.target.value)}
        placeholder={friend?.displayName}
        maxLength={64}
        autoFocus
        className="min-h-touch rounded-xl border border-edge bg-surface px-3 text-ink placeholder:text-muted focus:border-accent focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={saveNickname}
          className="min-h-touch flex-1 rounded-xl bg-accent px-4 text-sm font-medium text-on-accent active:bg-accent-press"
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          onClick={() => setNicknameEditing(false)}
          className="flex min-h-touch flex-1 items-center justify-center gap-2 rounded-xl bg-raised px-4 text-sm text-ink"
        >
          {t('common.cancel')}
        </button>
        {alias && (
          <button
            type="button"
            onClick={clearNickname}
            className="min-h-touch rounded-xl bg-raised px-4 text-sm text-danger"
          >
            {t('contactDetail.nicknameClear')}
          </button>
        )}
      </div>
    </div>
  );

  // Block severs the friendship server-side (docs/02 §3) and unblock does not
  // restore it — irreversible enough to earn the same two-step confirm as
  // remove-friend, and the copy says what actually happens.
  const blockConfirmView = (
    <div className="flex flex-col gap-2">
      <p role="alert" className="text-sm text-ink">
        {t('contactDetail.blockConfirm', { name: displayName })}
      </p>
      <button
        type="button"
        onClick={() => {
          setBlockConfirming(false);
          void toggleBlock();
        }}
        className="min-h-touch rounded-xl bg-danger px-4 text-sm font-medium text-ink"
      >
        {t('contactDetail.blockConfirmButton')}
      </button>
      <button
        type="button"
        onClick={() => setBlockConfirming(false)}
        className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-raised px-4 text-sm text-ink"
      >
        {t('common.cancel')}
      </button>
    </div>
  );

  const removeConfirmView = (
    <div className="flex flex-col gap-2">
      <p role="alert" className="text-sm text-ink">
        {t('contactDetail.removeFriendConfirm', { name: displayName })}
      </p>
      <button
        type="button"
        disabled={removing}
        onClick={() => void removeFriend()}
        className="min-h-touch rounded-xl bg-danger px-4 text-sm font-medium text-ink disabled:opacity-40"
      >
        {removing
          ? t('contactDetail.removingFriend')
          : t('contactDetail.removeFriendConfirmButton')}
      </button>
      <button
        type="button"
        disabled={removing}
        onClick={() => setRemoveConfirming(false)}
        className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-raised px-4 text-sm text-ink disabled:opacity-40"
      >
        {t('common.cancel')}
      </button>
    </div>
  );

  const historyList = (
    <>
      {calls.length === 0 && (
        <p className="text-sm text-muted">{t('contactDetail.noCalls')}</p>
      )}
      <ul className="flex flex-col gap-1">
        {calls.map((entry) => (
          <li key={entry.id} className="flex flex-col justify-center rounded-xl bg-surface p-3">
            <span className={entry.outcome === 'missed' ? 'text-danger' : 'text-ink'}>
              {describeCall(entry)}
            </span>
            <span className="text-xs text-muted">{formatTimestamp(entry.startedAt)}</span>
          </li>
        ))}
      </ul>
    </>
  );

  // --- Desktop pane variant --------------------------------------------------

  if (isDesktop) {
    return (
      <main className="flex min-h-full flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[560px] flex-col items-center px-6 py-10">
          {avatarSeed !== undefined && (
            <MercuryAvatar
              seed={candidateSeed(userId, avatarSeed)}
              size={96}
              className="rounded-full"
            />
          )}
          <h1 className="mt-4 max-w-full truncate text-2xl font-semibold text-ink">
            {displayName}
          </h1>
          {alias && friend && (
            <p className="mt-0.5 max-w-full truncate text-sm text-muted" data-testid="contact-aka">
              {t('contactDetail.aka', { name: friend.displayName })}
            </p>
          )}
          <p className="mt-1 flex items-center gap-2 text-sm text-muted">
            <PresenceDot status={effectivePresence} />
            {t(PRESENCE_LABEL_KEY[effectivePresence])}
          </p>
          <p
            className="mt-1 select-text break-all text-center font-mono text-xs text-muted"
            data-testid="contact-uuid"
          >
            {userId}
          </p>

          {noticeView && <div className="mt-3">{noticeView}</div>}

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={startCall}
              disabled={blocked}
              className="flex h-10 items-center justify-center gap-2 rounded-xl bg-accent px-5 text-base font-medium text-on-accent disabled:opacity-40 active:bg-accent-press"
            >
              <PhoneIcon /> {t('contactDetail.call')}
            </button>
            <button
              type="button"
              aria-pressed={isFav}
              aria-label={isFav ? t('contactDetail.removeFav') : t('contactDetail.addFav')}
              onClick={() => void setFavorite(userId, !isFav)}
              className={`flex h-10 w-10 items-center justify-center rounded-xl border border-edge bg-surface hover:bg-raised ${
                isFav ? 'text-warn' : 'text-ink'
              }`}
            >
              <StarIcon filled={isFav} />
            </button>
          </div>

          <div className="mt-4 flex w-full max-w-xs flex-col gap-2">
            {!nicknameEditing ? (
              <button
                type="button"
                onClick={startNicknameEdit}
                className="flex h-10 items-center justify-center gap-2 rounded-xl border border-edge bg-surface px-4 text-sm text-ink hover:bg-raised"
              >
                <PencilIcon />
                {t('contactDetail.nickname')}
                {alias && <span className="truncate text-muted">— {alias}</span>}
              </button>
            ) : (
              nicknameEditView
            )}

            {blocked || !blockConfirming ? (
              <button
                type="button"
                aria-pressed={blocked}
                onClick={() => (blocked ? void toggleBlock() : setBlockConfirming(true))}
                className={`flex h-10 items-center justify-center rounded-xl px-4 text-sm ${
                  blocked ? 'border border-edge bg-surface text-ink' : 'text-danger hover:bg-surface'
                }`}
              >
                {blocked ? t('contactDetail.unblock') : t('contactDetail.block')}
              </button>
            ) : (
              blockConfirmView
            )}
            {blockError && (
              <p role="status" className="text-sm text-warn">
                {blockError}
              </p>
            )}

            {!removeConfirming ? (
              <button
                type="button"
                onClick={() => setRemoveConfirming(true)}
                className="flex h-10 items-center justify-center rounded-xl px-4 text-sm text-danger hover:bg-surface"
              >
                {t('contactDetail.removeFriend')}
              </button>
            ) : (
              removeConfirmView
            )}
            {removeError && (
              <p role="status" className="text-sm text-warn">
                {removeError}
              </p>
            )}
          </div>

          <section
            aria-label={t('contactDetail.callHistory')}
            className="mt-8 flex w-full flex-col gap-2"
          >
            <h2 className="text-sm font-medium text-ink">{t('contactDetail.history')}</h2>
            {historyList}
          </section>
        </div>
      </main>
    );
  }

  // --- Compact full-screen variant (unchanged) -------------------------------

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <header className="flex items-center gap-3">
        <Link
          to="/contacts"
          aria-label={t('contactDetail.back')}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-xl bg-raised"
        >
          ←
        </Link>
        <div className="flex min-w-0 items-center gap-2">
          {avatarSeed !== undefined && (
            <MercuryAvatar
              seed={candidateSeed(userId, avatarSeed)}
              size={40}
              className="shrink-0 rounded-full"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <PresenceDot status={effectivePresence} />
              <h1 className="truncate text-xl font-semibold">{displayName}</h1>
            </div>
            {alias && friend && (
              <p className="truncate text-xs text-muted" data-testid="contact-aka">
                {t('contactDetail.aka', { name: friend.displayName })}
              </p>
            )}
          </div>
        </div>
      </header>

      <p className="break-all font-mono text-xs text-muted" data-testid="contact-uuid">
        {userId}
      </p>

      {noticeView}

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={startCall}
          disabled={blocked}
          className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-accent px-4 text-base font-medium text-ink disabled:opacity-40 active:bg-accent-press"
        >
          <PhoneIcon /> {t('contactDetail.call')}
        </button>

        <button
          type="button"
          aria-pressed={isFav}
          onClick={() => setFavorite(userId, !isFav)}
          className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-raised px-4 text-sm text-ink"
        >
          <StarIcon filled={isFav} /> {isFav ? t('contactDetail.removeFav') : t('contactDetail.addFav')}
        </button>

        {!nicknameEditing ? (
          <button
            type="button"
            onClick={startNicknameEdit}
            className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-raised px-4 text-sm text-ink"
          >
            <PencilIcon />
            {t('contactDetail.nickname')}
            {alias && <span className="truncate text-muted">— {alias}</span>}
          </button>
        ) : (
          nicknameEditView
        )}

        {/* Blocking severs the friendship server-side (docs/02 §3) and unblock
            does not restore it — irreversible enough to earn the same two-step
            confirm as remove-friend, and the copy says what actually happens.
            Unblock stays one tap: it destroys nothing. */}
        {blocked || !blockConfirming ? (
          <button
            type="button"
            aria-pressed={blocked}
            onClick={() => (blocked ? void toggleBlock() : setBlockConfirming(true))}
            className={`flex min-h-touch items-center justify-center gap-2 rounded-xl px-4 text-sm ${
              blocked ? 'bg-raised text-ink' : 'bg-surface text-danger'
            }`}
          >
            {blocked ? t('contactDetail.unblock') : t('contactDetail.block')}
          </button>
        ) : (
          blockConfirmView
        )}
        {blockError && (
          <p role="status" className="text-sm text-warn">
            {blockError}
          </p>
        )}

        {!removeConfirming ? (
          <button
            type="button"
            onClick={() => setRemoveConfirming(true)}
            className="flex min-h-touch items-center justify-center gap-2 rounded-xl bg-surface px-4 text-sm text-danger"
          >
            {t('contactDetail.removeFriend')}
          </button>
        ) : (
          removeConfirmView
        )}
        {removeError && (
          <p role="status" className="text-sm text-warn">
            {removeError}
          </p>
        )}
      </div>

      <section aria-label={t('contactDetail.callHistory')} className="flex flex-col gap-2">
        <h2 className="text-sm font-medium text-ink">{t('contactDetail.history')}</h2>
        {historyList}
      </section>
    </main>
  );
}
